import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import securityCameraImg from "@/assets/security-camera-lifestyle.jpg.asset.json";
import lumaInstallImg from "@/assets/luma-install-ovrc.png.asset.json";
import remotePlaybackImg from "@/assets/LUM-X20-Starlight.jpg.asset.json";

const bullets = [
  "Professional-grade 4K camera systems with AI analytics",
  "Smart locks and video doorbells from leading manufacturers",
  "24/7 professionally monitored alarm systems",
  "Cloud and on-site NVR recording with smart search",
  "Tight integration with your home automation platform",
  "Discreet camera placement designed with your architect",
];

export const Route = createFileRoute("/services/security")({
  head: () => ({
    meta: [
      { title: "Home Security Cameras & Smart Locks Install | Apex AV Denver" },
      { name: "description", content: "4K surveillance cameras, smart locks, video doorbells, and 24/7 monitored alarms for Denver homes — fully integrated with your automation platform." },
      { name: "keywords", content: "security camera installation Denver, home security Denver, surveillance camera installation Denver, 4K security cameras Denver, smart locks Denver, video doorbell installation Denver, monitored alarm Denver, access control Denver, NVR installation Denver, smart home security Denver" },
      { property: "og:title", content: "Security & Surveillance — Apex AV Denver" },
      { property: "og:description", content: "4K cameras, smart locks, and monitored alarms for Denver homes." },
      { property: "og:image", content: abs(securityCameraImg.url) },
      { property: "og:image:alt", content: "Professional 4K security camera installation on a modern Denver home by Apex AV" },
      { name: "twitter:image", content: abs(securityCameraImg.url) },
      { property: "og:url", content: "https://apexav.com/services/security" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Security & Surveillance — Apex AV Denver" },
      { name: "twitter:description", content: "4K cameras, smart locks, and monitored alarms for Denver homes." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services/security" }, { rel: "preload", as: "image", href: securityCameraImg.url, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "security",
            name: "Security & Surveillance",
            description: "4K camera systems, smart locks, and monitored alarms in Denver.",
            pageTitle: "Home Security Cameras & Smart Locks Install | Apex AV Denver",
            pageDescription:
              "4K surveillance cameras, smart locks, video doorbells, and 24/7 monitored alarms for Denver homes — fully integrated with your automation platform.",
            image: securityCameraImg.url,
            faqs: serviceFaqs["security"],
            serviceType: "Security & Surveillance",
            breadcrumbName: "Security & Surveillance",
            offerCatalog: bullets,
          }),
        ),
      },
    ],
  }),
  component: () => (
    <>
      <ServiceDetail
      faqs={serviceFaqs["security"]}
        topics={["security", "smart-home", "networking"]}
        eyebrow="Security"
        title="Eyes on every door, day and night."
        intro={<>Discreet <strong>security camera installation in Denver</strong>, 4K surveillance cameras, smart locks, video doorbells, and monitored alarms — fully integrated with your lighting, automation, and mobile app.</>}
        image={securityCameraImg.url}
        imageAlt="Professional 4K security camera installation on a modern Denver home by Apex AV"
        brands={["luma", "ubiquiti", "wattbox"]}
        bullets={bullets}
        features={[
          { title: "See everything", desc: "4K cameras with smart motion zones and AI person/vehicle detection." },
          { title: "Lock from anywhere", desc: "Grant temporary codes to guests, dog walkers, and contractors remotely." },
          { title: "Truly integrated", desc: "Doorbell rings, hallway lights come on, audio pauses — all automatic." },
        ]}
      />
      <section className="container mx-auto px-6 pb-20">
        <div className="rounded-2xl overflow-hidden shadow-card border border-border bg-card">
          <img
            src={lumaInstallImg.url}
            alt="Apex AV technician installing a Luma security camera and configuring remote monitoring with the OvrC app in Denver"
            loading="lazy"
            width={1280}
            height={720}
            className="w-full h-auto object-cover"
          />
          <div className="p-6 md:p-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-text">Installed by experts</p>
            <p className="mt-2 text-muted-foreground max-w-3xl">
              Our technicians position every camera for maximum coverage, then connect it to your remote monitoring app so you can check in from anywhere in Denver or across the country.
            </p>
          </div>
        </div>
      </section>
      <section className="container mx-auto px-6 pb-20">
        <div className="rounded-2xl overflow-hidden shadow-card border border-border bg-card">
          <img
            src={remotePlaybackImg.url}
            alt="Remote playback security camera app showing day and night vision footage for a Denver home"
            loading="lazy"
            width={1280}
            height={720}
            className="w-full h-auto object-cover"
          />
          <div className="p-6 md:p-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-text">Check in from anywhere</p>
            <p className="mt-2 text-muted-foreground max-w-3xl">
              Review recorded footage, switch between color and night-vision views, and scroll through timelines right from your phone — whether you are in Denver or traveling across the country.
            </p>
          </div>
        </div>
      </section>
    </>
  ),
});
